import { Award, Users, Clock, Heart, MapPin, Calendar, User, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export default function About() {
  const { language, t } = useLanguage();

  const stats = [
    {
      icon: Calendar,
      value: '2018',
      label: { en: 'Founded', fr: 'Fondé' }
    },
    {
      icon: Users,
      value: '1000+',
      label: { en: 'Patients', fr: 'Patients' }
    },
    {
      icon: Award,
      value: '8+',
      label: { en: 'Years of Experience', fr: 'Années d\'Expérience' }
    },
    {
      icon: Clock,
      value: '24H/24 7j/7',
      label: { en: 'Emergency Care', fr: 'Soins d\'Urgence' }
    },
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-amber-50 via-white to-blue-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t('Since 2018', 'Depuis 2018')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
            {t('About Clinique Mains Douces Plus', 'À Propos de Clinique Mains Douces Plus')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            {t(
              'Your trusted healthcare partner in Simbock with over 8 years of experience',
              'Votre partenaire de confiance en matière de soins de santé à Simbock avec plus de 8 ans d\'expérience'
            )}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
          {/* Image Column */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/equipe.jpeg"
                alt="Clinique Mains Douces Plus - Medical Center in Simbock"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent"></div>
            </div>
            
            {/* Floating Card - Location */}
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 flex items-center gap-2">
              <MapPin className="text-amber-600" size={20} />
              <div>
                <p className="font-bold text-blue-900">Tradex Simbock</p>
                <p className="text-xs text-gray-600">Yaoundé, Cameroun</p>
              </div>
            </div>

            {/* Floating Card - Director */}
            <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-xl p-4 max-w-[200px]">
              <div className="flex items-center gap-2 mb-1">
                <User className="text-amber-600" size={16} />
                <span className="text-xs font-semibold text-blue-900">
                  {t('Director', 'Directrice')}
                </span>
              </div>
              <p className="text-sm font-bold text-amber-600">Mme. ZEFO KITIO REGINE</p>
            </div>
          </div>

          {/* Content Column */}
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-4">
              {t('Our Story', 'Notre Histoire')}
            </h3>
            
            <div className="space-y-4 text-gray-700 mb-6">
              <p className="leading-relaxed">
                {t(
                  'Founded in 2018 as "Centre de Santé Mains Douces Plus" under the leadership of Mme. ZEFO KITIO REGINE, our healthcare facility has been serving the Yaoundé community for over 28 years. In 2025, we officially became Clinique Mains Douces Plus, marking our evolution into a fully equipped medical clinic.',
                  'Fondé en 2018 sous le nom de "Centre de Santé Mains Douces Plus" sous la direction de Mme. ZEFO KITIO REGINE, notre établissement de santé dessert la communauté de Yaoundé depuis plus de 28 ans. En 2025, nous sommes officiellement devenus Clinique Mains Douces Plus, marquant notre évolution vers une clinique médicale entièrement équipée.'
                )}
              </p>
              <p className="leading-relaxed">
                {t(
                  'Located in Tradex Simbock, we pride ourselves on offering a welcoming environment where every patient receives the personalized attention they deserve. Our team of qualified healthcare professionals combines medical expertise with compassion to ensure you receive the best possible care.',
                  'Située à Tradex Simbock, nous sommes fiers d\'offrir un environnement accueillant où chaque patient reçoit l\'attention personnalisée qu\'il mérite. Notre équipe de professionnels de santé qualifiés combine expertise médicale et compassion pour garantir que vous recevez les meilleurs soins possibles.'
                )}
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="bg-blue-100 px-4 py-2 rounded-lg">
                <p className="text-blue-900 font-semibold text-sm">
                  {t('Since 2018', 'Depuis 2018')}
                </p>
              </div>
              <div className="bg-amber-100 px-4 py-2 rounded-lg">
                <p className="text-amber-900 font-semibold text-sm">
                  {t('Qualified Staff', 'Personnel Qualifié')}
                </p>
              </div>
              <div className="bg-blue-100 px-4 py-2 rounded-lg">
                <p className="text-blue-900 font-semibold text-sm">
                  {t('24H/24 7j/7', '24H/24 7j/7')}
                </p>
              </div>
            </div>

            {/* Director Quote */}
            <div className="bg-white p-4 rounded-xl border-l-4 border-amber-500 mb-6">
              <p className="text-gray-700 italic text-sm">
                "{t(
                  'Our commitment to gentle care and medical excellence has defined us since 2018',
                  'Notre engagement envers des soins doux et l\'excellence médicale nous définit depuis 2018'
                )}"
              </p>
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition transform hover:scale-105 shadow-lg"
            >
              {t('Learn More About Us', 'En Savoir Plus Sur Nous')}
              <span>→</span>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg text-center hover:shadow-2xl transition group">
                <div className="bg-gradient-to-br from-blue-50 to-amber-50 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="text-blue-600" size={28} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-blue-900 mb-1">{stat.value}</p>
                <p className="text-sm text-gray-600 font-medium">
                  {language === 'en' ? stat.label.en : stat.label.fr}
                </p>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 text-white">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                {t('Why Choose Us', 'Pourquoi Nous Choisir')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Heart className="text-amber-300 flex-shrink-0 mt-1" size={20} />
                  <span>
                    {t('Gentle hands and caring hearts', 'Des mains douces et des cœurs attentionnés')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="text-amber-300 flex-shrink-0 mt-1" size={20} />
                  <span>
                    {t('24/7 emergency services available', 'Services d\'urgence disponibles 24h/24')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Award className="text-amber-300 flex-shrink-0 mt-1" size={20} />
                  <span>
                    {t('Over 8 years of medical excellence', 'Plus de 8 ans d\'excellence médicale')}
                  </span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-4">
                {t('Visit Us', 'Visitez-Nous')}
              </h3>
              <p className="text-blue-100 mb-4">
                {t(
                  'Conveniently located in Tradex Simbock, easily accessible from all neighborhoods of Yaoundé.',
                  'Idéalement situé à Tradex Simbock, facilement accessible depuis tous les quartiers de Yaoundé.'
                )}
              </p>
              <a
                href="tel:690640369"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-amber-50 transition"
              >
                <Phone size={18} />
                +237 690 640 369
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}